/**
 * Projects settings — Iter 425.n.2
 *
 * Owner-defined placeholder budget line presets. Standardises the
 * category-level line items (FIT001 · Fittings, ELEC001 · Electrical,
 * TRAV · Travel, GENERAL) used on sales orders. On the sales-doc
 * editor these appear as quick-add chips.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, X, Loader2, Tag, Trash2 } from "lucide-react";
import api from "../../lib/api";

export default function ProjectsSettings() {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ sku: "", description: "", default_budget: "", category: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/placeholder-presets");
      setPresets(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load presets.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const canSave = draft.sku.trim() && draft.description.trim();
  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await api.post("/placeholder-presets", {
        sku: draft.sku.trim(),
        description: draft.description.trim(),
        default_budget: parseFloat(draft.default_budget) || 0,
        category: draft.category.trim() || null,
      });
      toast.success(`Preset ${draft.sku} saved.`);
      setDraft({ sku: "", description: "", default_budget: "", category: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };
  const remove = async (pid) => {
    if (!window.confirm("Remove this preset?")) return;
    try {
      await api.delete(`/placeholder-presets/${pid}`);
      toast.success("Preset removed.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="projects-settings">
      <div>
        <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
          <Tag className="w-5 h-5 text-cyan-400" /> Placeholder presets
        </h1>
        <p className="text-sm text-[#647087] mt-1">
          Standard placeholder budget lines that any sales order can quick-add. Useful for category-level quoting
          (fittings, electrical, travel, general items) where the specific SKU is unknown at quote time. Actual spend
          on these categories is then routed to the placeholder via the Spend Assignments flow on the project.
        </p>
      </div>

      {/* Existing presets */}
      <section className="hv-card p-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold mb-2">
          Your presets · {presets.length}
        </div>
        {loading ? (
          <div className="text-xs text-[#647087] py-4 inline-flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </div>
        ) : presets.length === 0 ? (
          <div className="text-xs text-[#647087] italic py-2">
            No presets yet. Add the first one below.
          </div>
        ) : (
          <table className="w-full text-[11px]" data-testid="presets-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#647087] border-b border-white/5">
                <th className="text-left py-1 px-1.5">SKU</th>
                <th className="text-left py-1 px-1.5">Description</th>
                <th className="text-left py-1 px-1.5">Category</th>
                <th className="text-right py-1 px-1.5">Default budget</th>
                <th className="text-right py-1 px-1.5 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {presets.map((p) => (
                <tr key={p.id} className="border-b border-white/[0.03]" data-testid={`preset-row-${p.id}`}>
                  <td className="py-1 px-1.5 font-mono text-[#1FB6C1]">{p.sku}</td>
                  <td className="py-1 px-1.5 text-[#dde3ec]">{p.description}</td>
                  <td className="py-1 px-1.5 text-[#647087]">{p.category || "—"}</td>
                  <td className="py-1 px-1.5 text-right font-mono text-[#F59E0B]">
                    {p.default_budget ? p.default_budget.toLocaleString() : "—"}
                  </td>
                  <td className="py-1 px-1.5 text-right">
                    <button
                      onClick={() => remove(p.id)}
                      className="text-rose-400 hover:text-rose-300"
                      title="Remove preset"
                      data-testid={`preset-delete-${p.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Add new preset */}
      <section className="hv-card p-4" data-testid="preset-add">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold mb-2">Add preset</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div>
            <label className="text-[10px] text-[#647087] block mb-0.5">SKU</label>
            <input
              type="text"
              value={draft.sku}
              onChange={(e) => setDraft((d) => ({ ...d, sku: e.target.value }))}
              placeholder="FIT001"
              className="hv-input text-[11px] w-full"
              data-testid="preset-input-sku"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] text-[#647087] block mb-0.5">Description</label>
            <input
              type="text"
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              placeholder="Fittings and general plumbing"
              className="hv-input text-[11px] w-full"
              data-testid="preset-input-description"
            />
          </div>
          <div>
            <label className="text-[10px] text-[#647087] block mb-0.5">Category</label>
            <input
              type="text"
              value={draft.category}
              onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
              placeholder="fittings"
              className="hv-input text-[11px] w-full"
              data-testid="preset-input-category"
            />
          </div>
          <div>
            <label className="text-[10px] text-[#647087] block mb-0.5">Default budget (ex-VAT)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={draft.default_budget}
              onChange={(e) => setDraft((d) => ({ ...d, default_budget: e.target.value }))}
              placeholder="45000"
              className="hv-input text-[11px] w-full"
              data-testid="preset-input-budget"
            />
          </div>
        </div>
        <div className="mt-3">
          <button
            onClick={save}
            disabled={!canSave || saving}
            className="hv-btn hv-btn-primary text-[11px] inline-flex items-center gap-1"
            data-testid="preset-save"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Save preset
          </button>
        </div>
      </section>
    </div>
  );
}
